import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
	getSettingsUsers,
	UpdateStatusSettingsUser,
	getUserCapacity,
} from '../../../../../../services/settings/users';
import { useSearchParams } from 'react-router-dom';

const useUsers = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [bulkAction, setBulkAction] = useState([]);
	const [usersList, setUsersList] = useState([]);
	const [deselectRows, setDeselectRows] = useState(null);
	const [headerDropDown, setHeaderDropDown] = useState('All Users');
	const [loader, setLoder] = useState(false);
	const [sortDirection, setSortDirection] = useState({});
	// Seat usage from the subscribed plan. Null until loaded so the Add User
	// button is not disabled while we're still asking — a brief enabled button
	// that the API would reject is better than one that looks permanently broken
	// if the capacity call fails.
	const [capacity, setCapacity] = useState(null);

	const [params, setParams] = useState({
		page: Number(searchParams.get('page')) || 1,
		pageSize: Number(searchParams.get('pageSize')) || 10000,
		orderBy: searchParams.get('orderBy') || '',
		sortBy: searchParams.get('sortBy') || '',
		filterBy: searchParams.get('filterBy') || '',
		filterValue: searchParams.get('filterValue') || '',
	});
	const handleDeselectAll = (deselectAllRowsFn) => {
		setDeselectRows(() => deselectAllRowsFn);
	};

	const onRowClick = (id) => {};
	const sortDataList = (param) => {
		const newDirection = sortDirection[param.order_by] === 'desc' ? 'asc' : 'desc';
		setSortDirection({ [param.order_by]: newDirection });
		setParams((prev) => ({
			...prev,
			orderBy: param.order_by,
			sortBy: newDirection,
		}));
	};
	const handleFilterData = (type, value, key) => {
		setParams((prev) => ({
			...prev,
			filterBy: key,
			filterValue: value,
		}));
		setHeaderDropDown(type);
	};

	const handleDisable = async (e, id, status) => {
		try {
			e.stopPropagation();
			const data = {
				is_active: status,
			};
			const res = await UpdateStatusSettingsUser(data, id);
			if (res.success) {
				toast.success(res.message);
				getUsersList();
			} else {
				toast.error(res.message);
			}
		} catch (error) {
			toast.error(error.message);
		}
	};

	function mappedTableData(data) {
		return data?.map((ele) => {
			const isDraft = ele.status === 'draft';
			return {
				id: ele.organisation_user_id,
				user: JSON.stringify({
					id: ele.organisation_user_id,
					name: `${ele.first_name} ${ele.last_name}`.trim(),
					phone_number: ele.phone_number,
					// A draft still carries its @pharmnex.invalid placeholder. Showing
					// it would look like broken data to the admin, so the cell prompts
					// for the real address instead.
					email: isDraft ? '' : ele.email,
					is_active: ele.is_active,
					is_draft: isDraft,
				}),
				role: ele.role !== null && ele.role !== undefined ? ele.role.name : 'N/A',
				action: JSON.stringify({
					id: ele.organisation_user_id,
					is_active: ele.is_active,
					is_draft: isDraft,
					// Default users may be edited but never removed, and the owner may
					// not be deactivated. Carried through so the row can hide those
					// actions rather than offering a button the API rejects.
					is_system_generated: ele.is_system_generated,
					is_owner: ele.is_owner,
				}),
			};
		});
	}
	const getUsersList = async () => {
		try {
			setLoder(true);
			const { users } = await getSettingsUsers(params);
			const data = mappedTableData(users);
			setUsersList(data);
		} catch (error) {
			toast.error(error.message || 'something went wrong!');
		} finally {
			setLoder(false);
		}
	};

	// Refetched alongside the list rather than once on mount: adding a user or
	// activating a draft changes the seats consumed, and a stale count would
	// leave Add User enabled after the last seat was taken.
	const loadCapacity = async () => {
		try {
			const result = await getUserCapacity();
			// Only trust a well-formed response — the service layer resolves API
			// errors to the error body rather than throwing, so a failure would
			// otherwise be stored as if it were capacity data.
			setCapacity(typeof result?.used === 'number' ? result : null);
		} catch {
			// Non-fatal. Capacity stays null, Add User stays enabled, and the API
			// still enforces the limit.
			setCapacity(null);
		}
	};

	const setHeaderDropdownStatus = (filters) => {
		const statusMap = {
			active: 'Active Users',
			inactive: 'Inactive Users',
			draft: 'Draft Users',
			free: 'Free Users',
			paid: 'Paid Users',
		};
		if (filters) {
			setHeaderDropDown(statusMap[filters]);
		}
	};

	const setFilterFromUrl = () => {
		setSortDirection({
			[searchParams.get('orderBy')]: searchParams.get('sortBy') || '',
		});
		setHeaderDropdownStatus(searchParams.get('filterValue'));
	};

	useEffect(() => {
		setFilterFromUrl();
	}, []);

	const setUrlSearchParams = () => {
		const url = new URLSearchParams();
		url.set('page', params.page);
		url.set('pageSize', params.pageSize);
		url.set('orderBy', params.orderBy);
		url.set('sortBy', params.sortBy);
		url.set('filterBy', params.filterBy);
		url.set('filterValue', params.filterValue);
		setSearchParams(url);
	};

	useEffect(() => {
		setUrlSearchParams();
	}, [params]);

	useEffect(() => {
		getUsersList();
		loadCapacity();
	}, [searchParams]);
	return {
		usersList,
		getUsersList,
		capacity,
		headerDropDown,
		setHeaderDropDown,
		loader,
		sortDataList,
		sortDirection,
		handleFilterData,
		bulkAction,
		setBulkAction,
		onRowClick,
		handleDeselectAll,
		handleDisable,
	};
};
export default useUsers;
